import { useRef, useState } from "react";
import { X, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetOverlay, SheetPortal } from "@/components/ui/sheet";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { TicketForm, type TicketFormHandle } from "./ticket-form";
import type { TicketSheetProps } from "./ticket-form.types";

const FONT = "'Space Grotesk', system-ui, sans-serif";

export function TicketSheet({
  open,
  onClose,
  selectedRooms,
  prefillCity,
  prefillNeighborhoods,
  prefillPositions,
  dealId,
  dealName,
  companyName,
}: TicketSheetProps) {
  const formRef = useRef<TicketFormHandle>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!formRef.current) return;
    setIsSubmitting(true);
    try {
      // false = campos pendentes: o form exibe os erros e rola até o
      // primeiro; o sheet fica aberto para o usuário corrigir.
      const submitted = await formRef.current.submit();
      if (submitted) onClose();
    } catch {
      // toast is shown inside submit(); just stop the spinner
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetPortal>
        <SheetOverlay />
        <SheetPrimitive.Content
          className="fixed inset-y-0 right-0 z-50 flex flex-col bg-white shadow-2xl transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right data-[state=closed]:duration-300 data-[state=open]:duration-500"
          style={{
            width: "min(672px, 100vw)",
            fontFamily: FONT,
          }}
        >
          <SheetPrimitive.Title className="sr-only">
            Abrir Ticket
          </SheetPrimitive.Title>
          <SheetPrimitive.Description className="sr-only">
            Formulário para abertura de ticket de busca de escritório
          </SheetPrimitive.Description>

          <div
            className="flex-none flex items-center justify-between px-6 py-5"
            style={{ borderBottom: "1px solid #E8E6E3" }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: "#FFF8D6" }}
              >
                <ClipboardList
                  className="w-4.5 h-4.5"
                  style={{ color: "#92700C" }}
                />
              </div>
              <div>
                <h2 className="text-lg font-bold" style={{ color: "#1A1814" }}>
                  Abrir Ticket
                </h2>
                <p className="text-xs" style={{ color: "#78736D" }}>
                  {selectedRooms.length > 0
                    ? `${selectedRooms.length} ${selectedRooms.length === 1 ? "sala selecionada" : "salas selecionadas"}`
                    : "Nenhuma sala selecionada"}
                </p>
              </div>
            </div>
            <SheetPrimitive.Close
              className="rounded-lg p-2 transition-colors hover:bg-gray-100"
              style={{ color: "#78736D" }}
            >
              <X className="w-5 h-5" />
              <span className="sr-only">Fechar</span>
            </SheetPrimitive.Close>
          </div>

          <div
            className="flex-1 overflow-y-auto px-6 py-6"
            style={{ background: "#FDFBF7" }}
          >
            <TicketForm
              ref={formRef}
              rooms={selectedRooms}
              prefillCity={prefillCity}
              prefillNeighborhoods={prefillNeighborhoods}
              prefillPositions={prefillPositions}
              dealId={dealId}
              dealName={dealName}
              companyName={companyName}
              onSuccess={onClose}
            />
          </div>

          <div
            className="flex-none flex items-center justify-between px-6 py-4 bg-white"
            style={{ borderTop: "1px solid #E8E6E3" }}
          >
            <Button
              type="button"
              variant="outline"
              className="px-6 rounded-xl"
              style={{
                borderColor: "#E8E6E3",
                color: "#78736D",
                fontFamily: FONT,
              }}
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={isSubmitting}
              className="px-8 rounded-xl gap-2 font-semibold"
              style={{
                background: "#FFD900",
                color: "#1A1814",
                borderColor: "#FFD900",
                height: "44px",
                fontFamily: FONT,
                letterSpacing: "0.02em",
                opacity: isSubmitting ? 0.5 : 1,
              }}
              onClick={handleSubmit}
            >
              {isSubmitting ? "Enviando..." : "Enviar Ticket"}
            </Button>
          </div>
        </SheetPrimitive.Content>
      </SheetPortal>
    </Sheet>
  );
}
